import React, { useState, useEffect, useMemo } from 'react';
import { 
  CheckCircle, 
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  MapPin,
  Package,
  Building2,
  Download,
  Bed,
  Loader2,
  FileSpreadsheet,
  Search,
  Briefcase,
  Flag,
  Filter
} from 'lucide-react';
import { PilgrimGuideLink } from '../../types/pilgrimGuide';
import { Package as PackageType } from '../../types/package';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { useLanguage } from '../../contexts/LanguageContext';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import Select from 'react-select';

type Props = {
  links: PilgrimGuideLink[];
  packagesMap: Record<string, PackageType>;
};

const ITEMS_PER_PAGE_KEY = 'accommodation_tasks_items_per_page';
const PAGE_SIZE_OPTIONS = [10, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

export const AccommodationTasksList = ({
  links,
  packagesMap,
}: Props) => {
  const { translations } = useLanguage();
  const { tables } = useAccommodationStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedSize = localStorage.getItem(ITEMS_PER_PAGE_KEY);
    return savedSize ? parseInt(savedSize) : DEFAULT_PAGE_SIZE;
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRoomType, setSelectedRoomType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'completed' | 'pending' | 'partial'>('all');
  const [selectedOrganizers, setSelectedOrganizers] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'quick' | 'advanced'>('quick');
  const [exportProgress, setExportProgress] = useState(0);
  const userRole = localStorage.getItem('userRole');

  useEffect(() => {
    setCurrentPage(1);
  }, [links, searchTerm, selectedRoomType, selectedStatus, selectedOrganizers, selectedCountries, selectedPackages]);

  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size);
    setCurrentPage(1);
    localStorage.setItem(ITEMS_PER_PAGE_KEY, size.toString());
  };

  const getRoomTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '-';
    switch (type) {
      case 'double':
        return translations?.roomTypes?.double || 'Double';
      case 'triple':
        return translations?.roomTypes?.triple || 'Triple';
      case 'quadruple':
        return translations?.roomTypes?.quadruple || 'Quadruple';
      default:
        return type;
    }
  };

  // Memoize the roomNumber lookup function to avoid recalculations
  const roomNumberLookup = useMemo(() => {
    // Create a mapping of pilgrimId-hotelName to room info for quick lookup
    const lookup: Record<string, { roomNumber: number, roomType: string }> = {};
    
    for (const table of Object.values(tables)) {
      const hotelName = table.hn;
      if (!hotelName) continue;
      
      for (const room of table.r) {
        for (const bed of room.b) {
          if (bed.a) {
            for (const assignment of bed.a) {
              const key = `${assignment.pid}-${hotelName}`;
              lookup[key] = {
                roomNumber: room.n,
                roomType: room.t
              };
            }
          }
        }
      }
    }
    
    return lookup;
  }, [tables]);

  const getPilgrimRoomNumber = (pilgrimId: string, hotelName: string) => {
    if (!hotelName) return null;
    const key = `${pilgrimId}-${hotelName}`;
    return roomNumberLookup[key] || null;
  };

  // Extract unique organizers
  const organizers = useMemo(() => {
    const uniqueOrganizers = new Set<string>();
    links.forEach(link => {
      if (link.organizerName) {
        uniqueOrganizers.add(link.organizerName);
      }
    });
    return Array.from(uniqueOrganizers).sort();
  }, [links]);

  // Extract unique countries
  const countries = useMemo(() => {
    const uniqueCountries = new Set<string>();
    links.forEach(link => {
      if (link.residenceCountry) {
        uniqueCountries.add(link.residenceCountry);
      }
    });
    return Array.from(uniqueCountries).sort();
  }, [links]);
  
  // Extract unique packages
  const packages = useMemo(() => {
    const uniquePackages = new Set<string>();
    links.forEach(link => {
      if (link.packageName) {
        uniquePackages.add(link.packageName);
      }
    });
    return Array.from(uniquePackages).sort();
  }, [links]);

  // Memoize accommodation statuses
  const linkAccommodationStatuses = useMemo(() => {
    const statuses: Record<string, 'completed' | 'partial' | 'pending'> = {};
    
    links.forEach(link => {
      const pkg = packagesMap[link.packageName];
      if (!pkg) {
        statuses[link.id] = 'pending';
        return;
      }
      
      let accommodatedCount = 0;
      let totalHotels = 1; // Start with main Makkah hotel
      
      // Check main Makkah hotel
      const mainMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg.mainMakkahHotel);
      if (mainMakkahRoom) accommodatedCount++;
      
      // Check shifting Makkah hotel if exists
      if (pkg.shiftingMakkahHotel) {
        totalHotels++;
        const shiftingMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg.shiftingMakkahHotel);
        if (shiftingMakkahRoom) accommodatedCount++;
      }
      
      // Check Madinah hotel
      totalHotels++; // Add Madinah hotel
      const madinahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg.madinahHotel);
      if (madinahRoom) accommodatedCount++;
      
      // Return status based on accommodation count
      if (accommodatedCount === 0) {
        statuses[link.id] = 'pending';
      } else if (accommodatedCount === totalHotels) {
        statuses[link.id] = 'completed';
      } else {
        statuses[link.id] = 'partial';
      }
    });
    
    return statuses;
  }, [links, packagesMap, getPilgrimRoomNumber]);

  // Filter links based on all filters
  const filteredLinks = useMemo(() => {
    return links.filter((link) => {
      // Search by name or passport
      const matchesSearch = !searchTerm || 
        link.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.passportNumber.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by room type
      const matchesRoomType = !selectedRoomType || (
        (link.mainMakkahRoomType || link.main_makkah_roomType) === selectedRoomType ||
        (link.shiftingMakkahRoomType || link.shifting_makkah_roomType) === selectedRoomType ||
        (link.madinahRoomType || link.madinah_roomType) === selectedRoomType
      );

      // Filter by accommodation status
      const status = linkAccommodationStatuses[link.id] || 'pending';
      const matchesStatus = selectedStatus === 'all' || status === selectedStatus;

      // Filter by organizers (admin only)
      const matchesOrganizer = selectedOrganizers.length === 0 || 
        selectedOrganizers.includes(link.organizerName);

      // Filter by countries
      const matchesCountry = selectedCountries.length === 0 || 
        selectedCountries.includes(link.residenceCountry);

      // Filter by packages
      const matchesPackage = selectedPackages.length === 0 || 
        selectedPackages.includes(link.packageName);

      return matchesSearch && matchesRoomType && matchesStatus && 
             matchesOrganizer && matchesCountry && matchesPackage;
    });
  }, [
    links, 
    searchTerm, 
    selectedRoomType, 
    selectedStatus, 
    selectedOrganizers, 
    selectedCountries,
    selectedPackages,
    linkAccommodationStatuses
  ]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredLinks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLinks = useMemo(() => {
    return filteredLinks.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLinks, startIndex, itemsPerPage]);

  // Stats for filtered links
  const stats = useMemo(() => {
    return filteredLinks.reduce((acc, link) => {
      const status = linkAccommodationStatuses[link.id] || 'pending';
      acc[status]++;
      return acc;
    }, { completed: 0, partial: 0, pending: 0 });
  }, [filteredLinks, linkAccommodationStatuses]);

  // New function to fetch additional pilgrim data for export
  const fetchPilgrimDetails = async (pilgrimIds: string[]) => {
    try {
      const pilgrims: Record<string, { 
        Serial_Number?: string; 
        nusuk_group?: string;
        Gender?: string;
      }> = {};
      
      // Process pilgrim IDs in batches (Firestore limit)
      const batchSize = 30;
      const totalBatches = Math.ceil(pilgrimIds.length / batchSize);
      
      for (let i = 0; i < pilgrimIds.length; i += batchSize) {
        const batch = pilgrimIds.slice(i, i + batchSize);
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        const q = query(pilgrimsRef, where('__name__', 'in', batch));
        const snapshot = await getDocs(q);
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          pilgrims[doc.id] = {
            Serial_Number: data.Serial_Number,
            nusuk_group: data.nusuk_group,
            Gender: data.Gender
          };
        });
        
        // Update progress after each batch
        setExportProgress(Math.round(((i + batch.length) / pilgrimIds.length) * 100));
      }
      
      return pilgrims;
    } catch (error) {
      console.error('Error fetching pilgrim details:', error);
      return {};
    }
  };

  // Quick export function - without additional data
  const handleQuickExport = async () => {
    try {
      setIsExporting(true);
      setExportType('quick');
      
      // Prepare export data with now additional gender and nusuk_group fields
      
      // First, fetch the additional data
      const pilgrimIds = filteredLinks.map(link => link.pilgrimId);
      const pilgrimDetails = await fetchPilgrimDetails(pilgrimIds);
      
      const exportData = filteredLinks.map(link => {
        const pkg = packagesMap[link.packageName];
        const mainMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.mainMakkahHotel || '');
        const shiftingMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.shiftingMakkahHotel || '');
        const madinahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.madinahHotel || '');
        
        // Get additional details
        const details = pilgrimDetails[link.pilgrimId] || {};
        
        return {
          'Pilgrim Name': link.pilgrimName,
          'Passport Number': link.passportNumber,
          'Gender': details.Gender || link.gender || '-',
          'Nusuk Group': details.nusuk_group || link.nusukGroup || '-',
          'Country': link.residenceCountry,
          'Package': link.packageName,
          'Guide': link.guideName,
          'Organizer': link.organizerName,
          'Main Makkah Hotel': pkg?.mainMakkahHotel || '-',
          'Main Makkah Room Type': getRoomTypeLabel(link.mainMakkahRoomType || link.main_makkah_roomType),
          'Main Makkah Room': mainMakkahRoom?.roomNumber || '-',
          'Main Makkah Start': pkg?.mainMakkahStartDate || '-',
          'Main Makkah End': pkg?.mainMakkahEndDate || '-',
          'Shifting Makkah Hotel': pkg?.shiftingMakkahHotel || '-',
          'Shifting Makkah Room Type': getRoomTypeLabel(link.shiftingMakkahRoomType || link.shifting_makkah_roomType),
          'Shifting Makkah Room': shiftingMakkahRoom?.roomNumber || '-',
          'Shifting Makkah Start': pkg?.shiftingMakkahStartDate || '-',
          'Shifting Makkah End': pkg?.shiftingMakkahEndDate || '-',
          'Madinah Hotel': pkg?.madinahHotel || '-',
          'Madinah Room Type': getRoomTypeLabel(link.madinahRoomType || link.madinah_roomType),
          'Madinah Room': madinahRoom?.roomNumber || '-',
          'Madinah Start': pkg?.madinahStartDate || '-',
          'Madinah End': pkg?.madinahEndDate || '-',
          'Status': linkAccommodationStatuses[link.id] || 'pending'
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 20 }, // Passport Number
        { wch: 15 }, // Gender (new)
        { wch: 15 }, // Nusuk Group (new)
        { wch: 20 }, // Country
        { wch: 20 }, // Package
        { wch: 25 }, // Guide
        { wch: 25 }, // Organizer
        { wch: 20 }, // Main Makkah Hotel
        { wch: 15 }, // Main Makkah Room Type
        { wch: 12 }, // Main Makkah Room
        { wch: 12 }, // Main Makkah Start
        { wch: 12 }, // Main Makkah End
        { wch: 20 }, // Shifting Makkah Hotel
        { wch: 15 }, // Shifting Makkah Room Type
        { wch: 12 }, // Shifting Makkah Room
        { wch: 12 }, // Shifting Makkah Start
        { wch: 12 }, // Shifting Makkah End
        { wch: 20 }, // Madinah Hotel
        { wch: 15 }, // Madinah Room Type
        { wch: 12 }, // Madinah Room
        { wch: 12 }, // Madinah Start
        { wch: 12 }, // Madinah End
        { wch: 12 }, // Status
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Accommodation');
      XLSX.writeFile(wb, `accommodation-tasks-${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success(translations?.accommodationTasks?.exportSuccess || 'Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(translations?.accommodationTasks?.exportError || 'Failed to export data');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Advanced export function - with additional data
  const handleAdvancedExport = async () => {
    try {
      setIsExporting(true);
      setExportType('advanced');
      setExportProgress(5); // Start progress at 5%

      // Get all unique pilgrim IDs
      const pilgrimIds = filteredLinks.map(link => link.pilgrimId);
      
      // Fetch additional pilgrim details
      const pilgrimDetails = await fetchPilgrimDetails(pilgrimIds);
      
      // Prepare export data with the additional fields
      const exportData = filteredLinks.map(link => {
        const pkg = packagesMap[link.packageName];
        const mainMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.mainMakkahHotel || '');
        const shiftingMakkahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.shiftingMakkahHotel || '');
        const madinahRoom = getPilgrimRoomNumber(link.pilgrimId, pkg?.madinahHotel || '');
        
        // Get additional fields from pilgrim details
        const details = pilgrimDetails[link.pilgrimId] || {};
        
        return {
          'Pilgrim Name': link.pilgrimName,
          'Passport Number': link.passportNumber,
          'Gender': details.Gender || link.gender || '-', // Added field
          'Nusuk Group': details.nusuk_group || link.nusukGroup || '-', // Added field
          'Serial Number': details.Serial_Number || '-',
          'Country': link.residenceCountry,
          'Package': link.packageName,
          'Guide': link.guideName,
          'Organizer': link.organizerName,
          'Main Makkah Hotel': pkg?.mainMakkahHotel || '-',
          'Main Makkah Room Type': getRoomTypeLabel(link.mainMakkahRoomType || link.main_makkah_roomType),
          'Main Makkah Room': mainMakkahRoom?.roomNumber || '-',
          'Main Makkah Start': pkg?.mainMakkahStartDate || '-',
          'Main Makkah End': pkg?.mainMakkahEndDate || '-',
          'Shifting Makkah Hotel': pkg?.shiftingMakkahHotel || '-',
          'Shifting Makkah Room Type': getRoomTypeLabel(link.shiftingMakkahRoomType || link.shifting_makkah_roomType),
          'Shifting Makkah Room': shiftingMakkahRoom?.roomNumber || '-',
          'Shifting Makkah Start': pkg?.shiftingMakkahStartDate || '-',
          'Shifting Makkah End': pkg?.shiftingMakkahEndDate || '-',
          'Madinah Hotel': pkg?.madinahHotel || '-',
          'Madinah Room Type': getRoomTypeLabel(link.madinahRoomType || link.madinah_roomType),
          'Madinah Room': madinahRoom?.roomNumber || '-',
          'Madinah Start': pkg?.madinahStartDate || '-',
          'Madinah End': pkg?.madinahEndDate || '-',
          'Status': linkAccommodationStatuses[link.id] || 'pending'
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 20 }, // Passport Number
        { wch: 15 }, // Gender (new)
        { wch: 15 }, // Nusuk Group (new)
        { wch: 15 }, // Serial Number
        { wch: 20 }, // Country
        { wch: 20 }, // Package
        { wch: 25 }, // Guide
        { wch: 25 }, // Organizer
        { wch: 20 }, // Main Makkah Hotel
        { wch: 15 }, // Main Makkah Room Type
        { wch: 12 }, // Main Makkah Room
        { wch: 12 }, // Main Makkah Start
        { wch: 12 }, // Main Makkah End
        { wch: 20 }, // Shifting Makkah Hotel
        { wch: 15 }, // Shifting Makkah Room Type
        { wch: 12 }, // Shifting Makkah Room
        { wch: 12 }, // Shifting Makkah Start
        { wch: 12 }, // Shifting Makkah End
        { wch: 20 }, // Madinah Hotel
        { wch: 15 }, // Madinah Room Type
        { wch: 12 }, // Madinah Room
        { wch: 12 }, // Madinah Start
        { wch: 12 }, // Madinah End
        { wch: 12 }, // Status
      ];

      setExportProgress(90); // Update progress before generating file

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Accommodation');
      XLSX.writeFile(wb, `accommodation-tasks-detailed-${new Date().toISOString().split('T')[0]}.xlsx`);

      setExportProgress(100); // Complete progress
      toast.success(translations?.accommodationTasks?.exportSuccess || 'Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(translations?.accommodationTasks?.exportError || 'Failed to export data');
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Use react-select for multi-select dropdowns
  const countriesOptions = useMemo(() => 
    countries.map(country => ({ value: country, label: country })), 
    [countries]
  );
  
  const organizersOptions = useMemo(() => 
    organizers.map(organizer => ({ value: organizer, label: organizer })), 
    [organizers]
  );
  
  const packagesOptions = useMemo(() => 
    packages.map(pkg => ({ value: pkg, label: pkg })), 
    [packages]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b">
        <div className="flex flex-col gap-4">
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder={translations?.accommodationTasks?.searchPlaceholder || "Search by name or passport..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Room Type Filter */}
            <select
              value={selectedRoomType}
              onChange={(e) => setSelectedRoomType(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">{translations?.accommodationTasks?.filters?.allRoomTypes || 'All Room Types'}</option>
              <option value="double">{translations?.accommodationTasks?.filters?.double || 'Double'}</option>
              <option value="triple">{translations?.accommodationTasks?.filters?.triple || 'Triple'}</option>
              <option value="quadruple">{translations?.accommodationTasks?.filters?.quadruple || 'Quadruple'}</option>
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'all' | 'completed' | 'pending' | 'partial')}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">{translations?.accommodationTasks?.filters?.allStatuses || 'All Statuses'}</option>
              <option value="completed">{translations?.accommodationTasks?.filters?.completed || 'Completed'}</option>
              <option value="partial">{translations?.accommodationTasks?.filters?.partial || 'Partially Accommodated'}</option>
              <option value="pending">{translations?.accommodationTasks?.filters?.pending || 'Pending'}</option>
            </select>
          </div>

          {/* Advanced Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Organizer Filter - Admin only */}
            {userRole === 'admin' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {translations?.accommodationTasks?.filters?.organizers || 'Organizers'}
                </label>
                <Select
                  isMulti
                  options={organizersOptions}
                  placeholder="Select organizers..."
                  onChange={(selected) => 
                    setSelectedOrganizers(selected.map(option => option.value))
                  }
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </div>
            )}

            {/* Country Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.accommodationTasks?.filters?.countries || 'Countries'}
              </label>
              <Select
                isMulti
                options={countriesOptions}
                placeholder="Select countries..."
                onChange={(selected) => 
                  setSelectedCountries(selected.map(option => option.value))
                }
                className="basic-multi-select"
                classNamePrefix="select"
              />
            </div>

            {/* Package Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.accommodationTasks?.filters?.packages || 'Packages'}
              </label>
              <Select
                isMulti
                options={packagesOptions}
                placeholder="Select packages..."
                onChange={(selected) => 
                  setSelectedPackages(selected.map(option => option.value))
                }
                className="basic-multi-select"
                classNamePrefix="select"
              />
            </div>
          </div>

          {/* Stats Display */}
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{stats.completed}</span> {translations?.accommodationTasks?.stats?.completed || 'Completed'}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{stats.partial}</span> {translations?.accommodationTasks?.stats?.partial || 'Partial'}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{stats.pending}</span> {translations?.accommodationTasks?.stats?.pending || 'Pending'}
            </div>
          </div>

          {/* Active Filters Indicators */}
          {(searchTerm || selectedRoomType || selectedStatus !== 'all' || 
            selectedOrganizers.length > 0 || selectedCountries.length > 0 || 
            selectedPackages.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Filter size={16} className="text-primary-500" />
              <span className="text-sm text-gray-600">Active Filters:</span>
              
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Search: {searchTerm}
                </span>
              )}
              
              {selectedRoomType && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Room Type: {getRoomTypeLabel(selectedRoomType)}
                </span>
              )}
              
              {selectedStatus !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Status: {selectedStatus}
                </span>
              )}

              {selectedOrganizers.length > 0 && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Organizers: {selectedOrganizers.length}
                </span>
              )}

              {selectedCountries.length > 0 && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Countries: {selectedCountries.length}
                </span>
              )}

              {selectedPackages.length > 0 && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full">
                  Packages: {selectedPackages.length}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Export Buttons */}
        <div className="flex flex-wrap gap-2 mt-4">
          {/* Quick Export Button */}
          <button
            onClick={handleQuickExport}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-2 ${
              isExporting && exportType === 'quick' 
                ? 'bg-gray-300 text-gray-600' 
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
            } rounded-lg transition-colors`}
          >
            {isExporting && exportType === 'quick' ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download size={18} />
                <span>Quick Export</span>
              </>
            )}
          </button>

          {/* Advanced Export Button */}
          <button
            onClick={handleAdvancedExport}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-2 ${
              isExporting && exportType === 'advanced' 
                ? 'bg-gray-300 text-gray-600' 
                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            } rounded-lg transition-colors`}
          >
            {isExporting && exportType === 'advanced' ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FileSpreadsheet size={18} />
                <span>Advanced Export</span>
              </>
            )}
          </button>
        </div>

        {/* Export Progress Bar */}
        {isExporting && (
          <div className="mt-3">
            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${exportProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                {translations?.accommodationTasks?.headers?.pilgrim || 'Pilgrim'}
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                {translations?.accommodationTasks?.headers?.mainMakkah || 'Main Makkah Hotel'}
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                {translations?.accommodationTasks?.headers?.shiftingMakkah || 'Shifting Makkah Hotel'}
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                {translations?.accommodationTasks?.headers?.madinah || 'Madinah Hotel'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedLinks.length > 0 ? (
              paginatedLinks.map((link) => {
                const pkg = packagesMap[link.packageName];
                const mainMakkahRoom = pkg ? getPilgrimRoomNumber(link.pilgrimId, pkg.mainMakkahHotel || '') : null;
                const shiftingMakkahRoom = pkg && pkg.shiftingMakkahHotel ? 
                  getPilgrimRoomNumber(link.pilgrimId, pkg.shiftingMakkahHotel) : null;
                const madinahRoom = pkg ? getPilgrimRoomNumber(link.pilgrimId, pkg.madinahHotel || '') : null;
                
                return (
                  <tr key={link.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          link.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
                        }`}>
                          <User className={
                            link.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                          } size={20} />
                        </div>
                        <div>
                          <div className="font-medium">{link.pilgrimName}</div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                            <MapPin size={14} />
                            <span>{link.residenceCountry}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Package size={14} />
                            <span>{link.packageName}</span>
                          </div>
                          {link.nusukGroup && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>Nusuk Group: {link.nusukGroup}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Briefcase size={12} />
                            <span>{link.organizerName}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {pkg?.mainMakkahHotel || '-'}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <Bed size={14} />
                            <span>{getRoomTypeLabel(link.mainMakkahRoomType || link.main_makkah_roomType)}</span>
                          </div>
                          {mainMakkahRoom && (
                            <div className="text-sm text-gray-500">
                              {translations?.accommodationTasks?.room || 'Room'}: {mainMakkahRoom.roomNumber}
                            </div>
                          )}
                          {pkg?.mainMakkahStartDate && pkg?.mainMakkahEndDate && (
                            <div className="text-xs text-gray-400">
                              {pkg.mainMakkahStartDate} - {pkg.mainMakkahEndDate}
                            </div>
                          )}
                        </div>
                        {mainMakkahRoom ? (
                          <CheckCircle className="text-green-500" size={16} />
                        ) : (
                          <Clock className="text-yellow-500" size={16} />
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      {pkg?.shiftingMakkahHotel ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">
                              {pkg.shiftingMakkahHotel}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-2">
                              <Bed size={14} />
                              <span>{getRoomTypeLabel(link.shiftingMakkahRoomType || link.shifting_makkah_roomType)}</span>
                            </div>
                            {shiftingMakkahRoom && (
                              <div className="text-sm text-gray-500">
                                {translations?.accommodationTasks?.room || 'Room'}: {shiftingMakkahRoom.roomNumber}
                              </div>
                            )}
                            {pkg.shiftingMakkahStartDate && pkg.shiftingMakkahEndDate && (
                              <div className="text-xs text-gray-400">
                                {pkg.shiftingMakkahStartDate} - {pkg.shiftingMakkahEndDate}
                              </div>
                            )}
                          </div>
                          {shiftingMakkahRoom ? (
                            <CheckCircle className="text-green-500" size={16} />
                          ) : (
                            <Clock className="text-yellow-500" size={16} />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">
                          {translations?.common?.notAvailable || 'N/A'}
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {pkg?.madinahHotel || '-'}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <Bed size={14} />
                            <span>{getRoomTypeLabel(link.madinahRoomType || link.madinah_roomType)}</span>
                          </div>
                          {madinahRoom && (
                            <div className="text-sm text-gray-500">
                              {translations?.accommodationTasks?.room || 'Room'}: {madinahRoom.roomNumber}
                            </div>
                          )}
                          {pkg?.madinahStartDate && pkg?.madinahEndDate && (
                            <div className="text-xs text-gray-400">
                              {pkg.madinahStartDate} - {pkg.madinahEndDate}
                            </div>
                          )}
                        </div>
                        {madinahRoom ? (
                          <CheckCircle className="text-green-500" size={16} />
                        ) : (
                          <Clock className="text-yellow-500" size={16} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                  {translations?.accommodationTasks?.noResults || 'No matching results found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        {paginatedLinks.length > 0 ? (
          paginatedLinks.map((link) => {
            const pkg = packagesMap[link.packageName];
            const mainMakkahRoom = pkg ? getPilgrimRoomNumber(link.pilgrimId, pkg.mainMakkahHotel || '') : null;
            const shiftingMakkahRoom = pkg && pkg.shiftingMakkahHotel ? 
              getPilgrimRoomNumber(link.pilgrimId, pkg.shiftingMakkahHotel) : null;
            const madinahRoom = pkg ? getPilgrimRoomNumber(link.pilgrimId, pkg.madinahHotel || '') : null;
            
            return (
              <div key={link.id} className="p-4 border-b last:border-b-0 hover:bg-gray-50">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-full ${
                    link.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
                  }`}>
                    <User className={
                      link.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
                    } size={20} />
                  </div>
                  <div>
                    <div className="font-medium">{link.pilgrimName}</div>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <MapPin size={14} />
                      <span>{link.residenceCountry}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Package size={14} />
                      <span>{link.packageName}</span>
                    </div>
                    {link.nusukGroup && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>Nusuk Group: {link.nusukGroup}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Main Makkah */}
                  <div>
                    <div className="font-medium mb-1 text-sm">
                      {translations?.accommodationTasks?.headers?.mainMakkah || 'Main Makkah Hotel'}
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div>
                        <div className="text-sm">
                          {pkg?.mainMakkahHotel || '-'}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Bed size={12} />
                          <span>{getRoomTypeLabel(link.mainMakkahRoomType || link.main_makkah_roomType)}</span>
                        </div>
                        {mainMakkahRoom && (
                          <div className="text-xs text-gray-500 mt-1">
                            {translations?.accommodationTasks?.room || 'Room'}: {mainMakkahRoom.roomNumber}
                          </div>
                        )}
                      </div>
                      {mainMakkahRoom ? (
                        <CheckCircle className="text-green-500" size={16} />
                      ) : (
                        <Clock className="text-yellow-500" size={16} />
                      )}
                    </div>
                  </div>

                  {/* Shifting Makkah - if exists */}
                  {pkg?.shiftingMakkahHotel && (
                    <div>
                      <div className="font-medium mb-1 text-sm">
                        {translations?.accommodationTasks?.headers?.shiftingMakkah || 'Shifting Makkah Hotel'}
                      </div>
                      <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div>
                          <div className="text-sm">{pkg.shiftingMakkahHotel}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Bed size={12} />
                            <span>{getRoomTypeLabel(link.shiftingMakkahRoomType || link.shifting_makkah_roomType)}</span>
                          </div>
                          {shiftingMakkahRoom && (
                            <div className="text-xs text-gray-500 mt-1">
                              {translations?.accommodationTasks?.room || 'Room'}: {shiftingMakkahRoom.roomNumber}
                            </div>
                          )}
                        </div>
                        {shiftingMakkahRoom ? (
                          <CheckCircle className="text-green-500" size={16} />
                        ) : (
                          <Clock className="text-yellow-500" size={16} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Madinah */}
                  <div>
                    <div className="font-medium mb-1 text-sm">
                      {translations?.accommodationTasks?.headers?.madinah || 'Madinah Hotel'}
                    </div>
                    <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div>
                        <div className="text-sm">
                          {pkg?.madinahHotel || '-'}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Bed size={12} />
                          <span>{getRoomTypeLabel(link.madinahRoomType || link.madinah_roomType)}</span>
                        </div>
                        {madinahRoom && (
                          <div className="text-xs text-gray-500 mt-1">
                            {translations?.accommodationTasks?.room || 'Room'}: {madinahRoom.roomNumber}
                          </div>
                        )}
                      </div>
                      {madinahRoom ? (
                        <CheckCircle className="text-green-500" size={16} />
                      ) : (
                        <Clock className="text-yellow-500" size={16} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-8 text-center text-gray-500">
            {translations?.accommodationTasks?.noResults || 'No matching results found.'}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`p-1 rounded-lg ${
              currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronRight size={20} />
          </button>

          <span className="text-sm text-gray-700">
            {translations?.pagination?.page || 'Page'} {currentPage}{' '}
            {translations?.pagination?.of || 'of'} {totalPages || 1}
          </span>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`p-1 rounded-lg ${
              currentPage === totalPages || totalPages === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft size={20} />
          </button>
        </div>

        <div className="text-sm text-gray-500">
          {filteredLinks.length} {translations?.pagination?.items || 'items'}
        </div>
      </div>
    </div>
  );
};